import { useCallback } from "react";
import updateCanvas from "./updateCanvas";
import { svgCanvasConfig } from "./svgCanvasConfig";
export const useCanvasActions = ({ svgCanvasInstanceRef, svgCanvasRef, onSave }) => {
    const getSvgString = useCallback(() => {
        const canvas = svgCanvasInstanceRef.current;
        if (!canvas)
            return null;
        try {
            return canvas.getSvgString();
        }
        catch (error) {
            console.error("Error getting SVG string:", error);
            return null;
        }
    }, [svgCanvasInstanceRef]);
    const handleSave = useCallback(() => {
        const svg = getSvgString();
        if (!svg)
            return;
        const encoded = encodeURIComponent(svg);
        const dataUrl = `data:image/svg+xml;charset=utf-8,${encoded}`;
        onSave(dataUrl);
    }, [onSave, getSvgString]);
    const handleDownload = useCallback(() => {
        const svg = getSvgString();
        if (!svg)
            return;
        const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `floorplan-${new Date().getTime()}.svg`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [getSvgString]);
    const handleZoomIn = useCallback(() => {
        const canvas = svgCanvasInstanceRef.current;
        const cnvs = svgCanvasRef.current;
        if (!canvas || !cnvs)
            return;
        const newZoom = Math.min(canvas.getZoom() + 0.1, 3);
        canvas.setZoom(newZoom);
        updateCanvas({
            svgCanvas: canvas,
            cnvs,
            curConfig: svgCanvasConfig,
            center: false,
        });
    }, [svgCanvasInstanceRef, svgCanvasRef]);
    const handleZoomOut = useCallback(() => {
        const canvas = svgCanvasInstanceRef.current;
        const cnvs = svgCanvasRef.current;
        if (!canvas || !cnvs)
            return;
        const newZoom = Math.max(canvas.getZoom() - 0.1, 0.1);
        canvas.setZoom(newZoom);
        updateCanvas({
            svgCanvas: canvas,
            cnvs,
            curConfig: svgCanvasConfig,
            center: false,
        });
    }, [svgCanvasInstanceRef, svgCanvasRef]);
    const handleSetZoom = useCallback((zoom) => {
        const canvas = svgCanvasInstanceRef.current;
        const cnvs = svgCanvasRef.current;
        if (!canvas || !cnvs)
            return;
        canvas.setZoom(zoom / 100);
        updateCanvas({
            svgCanvas: canvas,
            cnvs,
            curConfig: svgCanvasConfig,
            center: false,
        });
    }, [svgCanvasInstanceRef, svgCanvasRef]);
    const handleSetFillColor = useCallback((color) => {
        const canvas = svgCanvasInstanceRef.current;
        if (!canvas)
            return;
        const selectedElements = canvas.getSelectedElements();
        if (selectedElements.length > 0) {
            selectedElements.forEach((elem) => {
                var _a;
                if (elem instanceof SVGElement) {
                    const tagName = (_a = elem.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                    const isLine = tagName === "line" || tagName === "polyline";
                    const attr = isLine ? "stroke" : "fill";
                    const opacityAttr = isLine ? "stroke-opacity" : "fill-opacity";
                    if (color === "none") {
                        elem.setAttribute(attr, "none");
                        elem.removeAttribute(opacityAttr);
                    }
                    else {
                        elem.setAttribute(attr, color);
                        elem.setAttribute(opacityAttr, "1");
                    }
                }
            });
            canvas.call("changed", selectedElements);
            if (svgCanvasRef.current) {
                updateCanvas({
                    svgCanvas: canvas,
                    cnvs: svgCanvasRef.current,
                    curConfig: svgCanvasConfig,
                    center: false,
                });
            }
        }
        else {
            const colorWithoutHash = color.startsWith("#") ? color.substring(1) : color;
            canvas.setColor("fill", colorWithoutHash, false);
        }
    }, [svgCanvasInstanceRef, svgCanvasRef]);
    const handleSetStrokeWidth = useCallback((width) => {
        const canvas = svgCanvasInstanceRef.current;
        if (!canvas)
            return;
        const selectedElements = canvas.getSelectedElements();
        if (selectedElements.length > 0) {
            selectedElements.forEach((elem) => {
                if (elem instanceof SVGElement) {
                    elem.setAttribute("stroke-width", width.toString());
                }
            });
            canvas.call("changed", selectedElements);
            if (svgCanvasRef.current) {
                updateCanvas({
                    svgCanvas: canvas,
                    cnvs: svgCanvasRef.current,
                    curConfig: svgCanvasConfig,
                    center: false,
                });
            }
        }
    }, [svgCanvasInstanceRef, svgCanvasRef]);
    return {
        handleSave,
        handleDownload,
        handleZoomIn,
        handleZoomOut,
        handleSetZoom,
        handleSetFillColor,
        handleSetStrokeWidth,
    };
};
